import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpResponse } from '@angular/common/http';

import { Observable, Subject, of } from 'rxjs';
import { tap, switchMap } from 'rxjs/operators';

import { SessionStorageService } from '@core/services/util/session-storage.service';
import { PlatformService } from '@core/services/util/platform.service';
import { LoginService } from '@core/services/login/login.service';
import { StorageService } from '@core/services/util/storage.service';
import { UtilityService } from '@core/services/util/utility.service';
import { CustomerService } from './customer/customer.service';
import { environment } from '@environment';

@Injectable({
    providedIn: 'root'
})
export class HttpInterceptorService {
    refreshTokenInProgress = false;

    tokenRefreshedSource = new Subject();
    tokenRefreshed$ = this.tokenRefreshedSource.asObservable();
    requestsToBeCached = {
        [`${environment.apiUrl}/static_webpage/show`]: { expiryInSeconds: Infinity },
        [`${environment.catalogUrl}/catalog/v1/banners`]: { expiryInSeconds: Infinity },
        [`${environment.apiUrl}/order_details`]: { expiryInSeconds: Infinity } //only for Download Invoice API
    };
    private cache = {};
    s3UploadUrl = 'customer-order-feedback-images';

    constructor(
        private loginService: LoginService,
        private sessionStorage: SessionStorageService,
        private platformService: PlatformService,
        private storageService: StorageService,
        private utilityService: UtilityService,
        private customerService: CustomerService
    ) {}

    addAuthHeader(request) {
        const authHeader = this.utilityService.getAuthHeaders('');
        if (authHeader) {
            return request.clone({
                setHeaders: authHeader
            });
        }
        return request;
    }

    refreshToken() {
        if (this.refreshTokenInProgress) {
            return new Observable(observer => {
                this.tokenRefreshed$.subscribe(() => {
                    observer.next();
                    observer.complete();
                });
            });
        } else {
            this.refreshTokenInProgress = true;

            return this.loginService.updateToken().pipe(
                tap((data: any) => {
                    if (data && data.meta && data.meta.code === 200) {
                        if (this.storageService.get('session-id') && this.storageService.get('phone_no')) {
                            this.storageService.set('session-id', data.auth_token);
                        } else {
                            this.storageService.set('unsigned_session_id', data.auth_token);
                        }
                        this.tokenRefreshedSource.next(true);
                    } else {
                        this.tokenRefreshedSource.unsubscribe();
                    }
                    this.refreshTokenInProgress = false;
                })
            );
        }
    }

    getMatchingCacheKey(request) {
        const url = Object.keys(this.requestsToBeCached).filter(req => String(request).includes(req));
        return url.length ? url[0] : null;
    }

    cacheNotExpired(request) {
        const currentTimeInSeconds = Math.floor(Date.now() / 1000);
        const cachedTimeInSeconds = request.timestampInSeconds;
        const expiry = this.requestsToBeCached[request.matchingCacheKey]['expiryInSeconds'];
        return currentTimeInSeconds - cachedTimeInSeconds < expiry;
    }

    cacheResponse(event, request) {
        const matchingCacheKey = this.getMatchingCacheKey(request.url);
        if (!matchingCacheKey) return;

        const obj = {};
        obj['response'] = event.clone();
        obj['timestampInSeconds'] = Math.floor(Date.now() / 1000);
        obj['matchingCacheKey'] = matchingCacheKey;
        this.cache[request.urlWithParams] = obj;
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (this.platformService.isServer()) {
            return next.handle(request);
        }

        if (!request.url.includes(this.s3UploadUrl)) {
            // do not send these params to s3 requests
            const utm_source = this.sessionStorage.get('utm_source');
            const utm_medium = this.sessionStorage.get('utm_medium');
            const utm_campaign = this.sessionStorage.get('utm_campaign');

            request = request.clone({
                setParams: {
                    ...(utm_source && { utm_source: utm_source }),
                    ...(utm_medium && { utm_medium: utm_medium }),
                    ...(utm_campaign && { utm_campaign: utm_campaign }),
                    origin: environment.project_name,
                    platform: 'web'
                }
            });
        }

        const cachedRequest = this.cache[request.urlWithParams];

        if (cachedRequest && this.cacheNotExpired(cachedRequest)) {
            return of(cachedRequest.response.clone());
        } else {
            return next.handle(request).pipe(
                tap(
                    (event: any) => {
                        if (event instanceof HttpResponse) {
                            if (event.body && event.body.meta) {
                                if (event.body.meta.code === 498) {
                                    return this.refreshToken()
                                        .pipe(
                                            switchMap(() => {
                                                request = this.addAuthHeader(request);
                                                return next.handle(request);
                                            })
                                        )
                                        .subscribe();
                                }
                                if (event.body.meta.code === 200) {
                                    if (
                                        request.url.includes(environment.accountsApiUrl + '/customers/sign_in') ||
                                        request.url.includes(environment.accountsApiUrl + '/customers/sign_out')
                                    ) {
                                        this.cache = {};
                                    } else {
                                        this.cacheResponse(event, request);
                                    }
                                }
                            } else {
                                if (event.status === 200) {
                                    // for responses that do not contain meta; eg: html response
                                    this.cacheResponse(event, request);
                                }
                            }
                        }
                    },
                    (errorResponse: any) => {
                        if (errorResponse.error && errorResponse.error.meta) {
                            if (errorResponse.error.meta.code === 401) {
                                this.loginService.clearSessions(); //explicitly to delete unsigned_session_id
                                this.customerService.afterLogoutTask();
                            }

                            if (errorResponse.error.meta.code === 498) {
                                return this.refreshToken()
                                    .pipe(
                                        switchMap(() => {
                                            request = this.addAuthHeader(request);
                                            return next.handle(request);
                                        })
                                    )
                                    .subscribe();
                            }
                        }
                    }
                )
            );
        }
    }
}
